import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const useEmployees = (API_BASE, t) => {
  const [employeeList, setEmployeeList] = useState([]);
  const [originatorNames, setOriginatorNames] = useState({});

  const fetchAllEmployees = async () => {
    try {
      const response = await apiFetch(
        `${API_BASE.replace("/calendar", "")}/employees`,
        { method: "GET" }
      );
      const res = await response.json();

      let employees = [];
      if (Array.isArray(res)) {
        employees = res;
      } else if (res.data?.success && Array.isArray(res.data.data)) {
        employees = res.data.data;
      } else if (res.data?.employees && Array.isArray(res.data.employees)) {
        employees = res.data.employees;
      }

      const normalizedEmployees = employees
        .map((emp) => ({
          id: emp.id || emp.infinityCode,
          infinityCode: emp.infinityCode,
          name:
            emp.name ||
            emp.sr ||
            emp.arabicName ||
            emp.englishName ||
            t.unknownUser,
          email: emp.email,
          department: emp.department || "N/A",
          position: emp.position || emp.jobTitle || "N/A",
        }))
        .filter((emp) => emp.email);

      setEmployeeList(normalizedEmployees);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      setEmployeeList([]);
    }
  };

  const fetchEmployeeByInfinityCode = async (infinityCode) => {
    if (!infinityCode) return t.unknownUser;

    try {
      const response = await apiFetch(
        `${API_BASE.replace("/calendar", "")}/employees/select/${infinityCode}`,
        { method: "GET" }
      );
      const res = await response.json();

      // Backend returns { success, employee } at the top level — no `.data` wrapper.
      if (res.success && res.employee) {
        return res.employee.sr || res.employee.arabicName || res.employee.email || t.unknownUser;
      }
    } catch (err) {
      console.error(`Failed to fetch employee with infinityCode ${infinityCode}:`, err);
    }
    // Never fall back to the current viewer's own name here — that would
    // misattribute a different person's event as organized by the viewer.
    return t.unknownUser;
  };

  const loadOriginatorName = async (infinityCode) => {
    if (!infinityCode) return t.unknownUser;

    if (originatorNames[infinityCode]) {
      return originatorNames[infinityCode];
    }

    const name = await fetchEmployeeByInfinityCode(infinityCode);

    setOriginatorNames((prev) => ({ ...prev, [infinityCode]: name }));
    return name;
  };

  const getEmployeeSuggestions = (suggestionFilter, currentAttendeesString) => {
    if (!suggestionFilter || suggestionFilter.length < 1) return [];

    const currentAttendees = (currentAttendeesString || "")
      .split(",")
      .map((email) => email.trim().toLowerCase());
    const filter = suggestionFilter.toLowerCase();

    return employeeList
      .filter((emp) => {
        if (currentAttendees.includes(emp.email.toLowerCase())) return false;
        return (
          emp.name.toLowerCase().includes(filter) ||
          emp.email.toLowerCase().includes(filter)
        );
      })
      .slice(0, 8);
  };

  return {
    employeeList,
    originatorNames,
    fetchAllEmployees,
    loadOriginatorName,
    getEmployeeSuggestions,
  };
};

export default useEmployees;
